/**
 * downscaleForDetection — canvas-based frame downscaler for SCRFD detection.
 *
 * PR #4 — adaptive-frame-pacing
 *
 * ╔═══════════════════════════════════════════════════════════════════════════╗
 * ║  DETECTION USE ONLY — NEVER feed the output to inferEmbedding.          ║
 * ║                                                                         ║
 * ║  MobileFaceNet's 112×112 input is resized from the face CROP.  If the  ║
 * ║  source frame is downscaled, the crop has fewer raw pixels, shifting   ║
 * ║  the embedding vector and causing false rejects against vectors that    ║
 * ║  were enrolled at full resolution.                                     ║
 * ║                                                                         ║
 * ║  Always pass the original full-resolution frame to inferEmbedding.    ║
 * ╚═══════════════════════════════════════════════════════════════════════════╝
 *
 * OQ-2 resolution: uses OffscreenCanvas when available; falls back to
 * document.createElement('canvas') for environments without OffscreenCanvas
 * (e.g. Samsung A12 system WebView on Android 11).
 */
import type { ImageFrame } from './vendor/index.js';

/**
 * Downscale `frame` to `{Math.round(width * scale), Math.round(height * scale)}`
 * using the browser's 2D canvas API for hardware-accelerated scaling.
 *
 * @param frame  Source RGBA frame (full resolution).
 * @param scale  Scale factor in (0, 1]. Must be > 0.
 * @returns      The original `frame` unchanged when `scale === 1.0` (no copy,
 *               no canvas allocation). A new ImageFrame when `scale < 1`.
 */
export function downscaleForDetection(frame: ImageFrame, scale: number): ImageFrame {
  // Defensive guard (M2 from PR #4 judgment-day review): reject scales outside
  // (0, 1]. scale <= 0 would create a 0×0 canvas (browser crash). scale > 1
  // would upscale, which this helper deliberately does not support — upscaling
  // for detection has no CPU benefit and complicates the bbox rescale contract.
  if (!(scale > 0 && scale <= 1)) {
    throw new RangeError(
      `[downscaleForDetection] scale must be in (0, 1], got ${scale}`,
    );
  }

  // Fast path: no-op — guarantees same reference, zero allocation.
  if (scale === 1.0) return frame;

  const srcW = frame.width;
  const srcH = frame.height;
  const dstW = Math.round(srcW * scale);
  const dstH = Math.round(srcH * scale);

  // ── Source canvas: render original pixels ──────────────────────────────────
  const srcCanvas = createCanvas(srcW, srcH);
  const srcCtx    = getContext2d(srcCanvas);

  // ImageData requires Uint8ClampedArray<ArrayBuffer>. The vendor ImageFrame type uses
  // `ArrayBufferLike` which includes SharedArrayBuffer; cast to ArrayBuffer is safe here
  // because all canvas pixel data in browsers originates from regular ArrayBuffers.
  const clampedData = (
    frame.data instanceof Uint8ClampedArray
      ? frame.data
      : new Uint8ClampedArray(frame.data.buffer, frame.data.byteOffset, frame.data.byteLength)
  ) as Uint8ClampedArray<ArrayBuffer>;

  srcCtx.putImageData(new ImageData(clampedData, srcW, srcH), 0, 0);

  // ── Destination canvas: draw source scaled to dst dimensions ───────────────
  const dstCanvas = createCanvas(dstW, dstH);
  const dstCtx    = getContext2d(dstCanvas);

  // Enable bilinear interpolation for better quality on small faces.
  dstCtx.imageSmoothingEnabled = true;
  // imageSmoothingQuality is a modern API — set only if available.
  if ('imageSmoothingQuality' in dstCtx) {
    (dstCtx as CanvasRenderingContext2D).imageSmoothingQuality = 'medium';
  }

  // drawImage accepts both HTMLCanvasElement and OffscreenCanvas as source.
  dstCtx.drawImage(srcCanvas as HTMLCanvasElement, 0, 0, dstW, dstH);

  // ── Read scaled pixels back ────────────────────────────────────────────────
  const scaled = dstCtx.getImageData(0, 0, dstW, dstH);

  return { data: scaled.data, width: dstW, height: dstH, format: 'rgba' };
}

// ── Canvas factory (OQ-2 resolution) ─────────────────────────────────────────

/**
 * Create a canvas of the given size.
 * Prefers OffscreenCanvas for worker-thread compatibility and reduced DOM overhead.
 * Falls back to document.createElement('canvas') when OffscreenCanvas is unavailable.
 */
function createCanvas(w: number, h: number): HTMLCanvasElement | OffscreenCanvas {
  if (typeof OffscreenCanvas !== 'undefined') {
    return new OffscreenCanvas(w, h);
  }
  const canvas   = document.createElement('canvas');
  canvas.width   = w;
  canvas.height  = h;
  return canvas;
}

function getContext2d(
  canvas: HTMLCanvasElement | OffscreenCanvas,
): CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D {
  // Explicit instanceof narrowing prevents TypeScript from confusing the two getContext overloads.
  if (canvas instanceof HTMLCanvasElement) {
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) throw new Error('[downscaleForDetection] Failed to acquire 2D context');
    return ctx;
  }
  const ctx = (canvas as OffscreenCanvas).getContext('2d');
  if (!ctx) throw new Error('[downscaleForDetection] Failed to acquire 2D context');
  return ctx as OffscreenCanvasRenderingContext2D;
}
